import { useSyncExternalStore } from 'react';
import dayjs from 'dayjs';
import { DEMO_VERSION, demoState } from './hrDemo';
import {
  CONTRACT_PREFIX, STAGES, candById, candidateDupCheck, currentContract, empById, empContracts, empProbationLimit, nextCode, nextEmpCode,
  offTasksFor, offerNeedsApproval, positionById, probationEndOf, reqById, reqHired, toDay, validateContract, validateRequisition,
  type Candidate, type Contract, type ContractDraft, type DocKey, type Employee, type HrState, type LeaveType, type License, type Log,
  type Offboarding, type OffTask, type ReqDraft, type Requisition, type Stage, type AssetItem, type RecordItem, type School, unreturnedAssets,
  MOVE_TYPE, salaryAt, validateMove, type Dependent, type MoveDraft, type MoveItem, LEAVE_KIND, validateLeave, type LeaveRecord,
  CANDIDATE_RETENTION_DAYS, daysBetween, anonymized, licenseBlocker, noShowCheck, pendingInterview, retentionDue, stageAt, validateCandidateLicense, validateInterview,
  type CandidateLicense, type Interview,
} from './hrLogic';

/**
 * Dữ liệu nhân sự – tuyển dụng lưu ở localStorage (chạy front-end trước, sau này thay từng hàm hrApi bằng API).
 * Lần đầu mở sẽ tự nạp dữ liệu demo của chuỗi thẩm mỹ viện.
 */

const KEY = 'khothi.hr.v1';

/** Áp các quyết định điều chuyển đã tới ngày hiệu lực; luân chuyển hết hạn thì trả về vị trí cũ */
function applyDueMoves(s: HrState): HrState {
  const d = toDay();
  let changed = false;
  const employees = s.employees.map(e => {
    let x = e;
    (e.moves || []).forEach(m => {
      if (!m.applied && m.date <= d) {
        changed = true;
        x = { ...x, positionId: m.toPositionId, branchId: m.toBranchId, moves: (x.moves || []).map(y => (y.id === m.id ? { ...y, applied: true } : y)), log: [...x.log, { at: Date.now(), by: m.by, text: `Áp dụng quyết định ${m.decisionNo}` }] };
      }
      if (m.applied && m.type === 'rotation' && m.until && m.until < d && x.positionId === m.toPositionId && x.branchId === m.toBranchId) {
        changed = true;
        x = { ...x, positionId: m.fromPositionId, branchId: m.fromBranchId, moves: (x.moves || []).map(y => (y.id === m.id ? { ...y, type: 'transfer' as const, reason: `${y.reason || ''} (đã kết thúc luân chuyển)`.trim() } : y)), log: [...x.log, { at: Date.now(), by: m.by, text: `Kết thúc luân chuyển ${m.decisionNo}, trở về vị trí cũ` }] };
      }
    });
    return x;
  });
  return changed ? { ...s, employees } : s;
}

/** Hồ sơ đã đóng thì các lịch phỏng vấn còn chờ tự hủy */
const dropPending = (c: Candidate): Candidate => (c.outcome !== 'open' && (c.interviews || []).some(i => i.status === 'scheduled')
  ? { ...c, interviews: c.interviews!.map(i => (i.status === 'scheduled' ? { ...i, status: 'cancelled' as const, resolvedAt: c.closedAt || Date.now(), resolvedNote: 'Hồ sơ đã đóng' } : i)) }
  : c);

function load(): HrState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw);
      const stale = (s?.demoVersion || 0) < DEMO_VERSION && Array.isArray(s?.employees) && s.employees.every((e: { id: string }) => e.id.startsWith('demo-'));
      if (s?.v === 1 && Array.isArray(s.employees) && !stale) return applyDueMoves({ ...s, candidates: (s.candidates || []).map(dropPending) });
    }
  } catch { /* dữ liệu hỏng → nạp lại demo */ }
  const s = demoState();
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* bỏ qua */ }
  return s;
}

let state: HrState = load();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

function set(fn: (s: HrState) => HrState): boolean {
  const next = fn(state);
  if (next === state) return true;
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { return false; }
  state = next;
  emit();
  return true;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    if (e.key === KEY && e.newValue) { state = load(); emit(); }
  });
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const useHr = () => useSyncExternalStore(subscribe, () => state);
export const getHr = () => state;

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const today = () => toDay();
export interface Result { ok: boolean; errors?: string[]; warnings?: string[]; id?: string }
const FULL = ['Bộ nhớ trình duyệt đầy, không lưu được'];
const fail = (...errors: string[]): Result => ({ ok: false, errors });

const logged = <T extends { log: Log[] }>(x: T, by: string, text: string): T => ({ ...x, log: [...x.log, { at: Date.now(), by, text }] });
const patchEmp = (s: HrState, id: string, fn: (e: Employee) => Employee): HrState => ({ ...s, employees: s.employees.map(e => (e.id === id ? fn(e) : e)) });
const patchContract = (s: HrState, id: string, fn: (c: Contract) => Contract): HrState => ({ ...s, contracts: s.contracts.map(c => (c.id === id ? fn(c) : c)) });
const patchOff = (s: HrState, id: string, fn: (o: Offboarding) => Offboarding): HrState => ({ ...s, offboardings: s.offboardings.map(o => (o.id === id ? fn(o) : o)) });
const patchReq = (s: HrState, id: string, fn: (r: Requisition) => Requisition): HrState => ({ ...s, requisitions: s.requisitions.map(r => (r.id === id ? fn(r) : r)) });
const patchCand = (s: HrState, id: string, fn: (c: Candidate) => Candidate): HrState => ({ ...s, candidates: s.candidates.map(c => (c.id === id ? dropPending(fn(c)) : c)) });
/** Ghi kết quả vòng phỏng vấn hiện tại: lịch hẹn đang chờ chuyển sang "đã phỏng vấn" */
const finishInterview = (c: Candidate, at: number): Candidate => {
  const p = pendingInterview(c);
  return p ? { ...c, interviews: (c.interviews || []).map(i => (i.id === p.id ? { ...i, status: 'done' as const, resolvedAt: at } : i)) } : c;
};
/** Chưa tới giờ hẹn thì chưa có kết quả phỏng vấn */
const notYetInterviewed = (c: Candidate) => {
  const p = pendingInterview(c);
  return p && p.at > Date.now() ? `Lịch phỏng vấn lúc ${dayjs(p.at).format('HH:mm DD/MM')} chưa diễn ra` : null;
};

type ListKey = 'schools' | 'workHistory' | 'assets' | 'records' | 'dependentList';
type ListItem<K extends ListKey> = NonNullable<Employee[K]>[number];
const LIST_LABEL: Record<ListKey, string> = { schools: 'học vấn', workHistory: 'kinh nghiệm làm việc', assets: 'tài sản cấp phát', records: 'khen thưởng / vi phạm', dependentList: 'người phụ thuộc' };

export type EmployeeDraft = Omit<Employee, 'id' | 'code' | 'createdAt' | 'log' | 'docs' | 'licenses' | 'ins'> & { id?: string; docs?: Employee['docs'] };

export const hrApi = {
  /* ============================== Hồ sơ nhân sự ============================== */

  saveEmployee(d: EmployeeDraft, by: string): Result {
    const errors: string[] = [];
    if (!d.name.trim()) errors.push('Nhập họ tên');
    if (!d.phone.replace(/\D/g, '')) errors.push('Nhập số điện thoại');
    if (!d.branchId) errors.push('Chọn chi nhánh');
    if (!positionById(state, d.positionId)) errors.push('Chọn vị trí');
    if (!d.hireDate) errors.push('Nhập ngày vào làm');
    const phone = d.phone.replace(/\D/g, '');
    const dup = state.employees.find(e => e.id !== d.id && e.phone.replace(/\D/g, '') === phone && (!e.leftDate || e.leftDate >= today()));
    if (dup) errors.push(`Số điện thoại trùng với ${dup.name} (${dup.code})`);
    if (d.cccd && state.employees.some(e => e.id !== d.id && e.cccd && e.cccd === d.cccd && (!e.leftDate || e.leftDate >= today()))) errors.push('Số CCCD đã có trong hồ sơ nhân sự khác');
    if (errors.length) return { ok: false, errors };
    const clean = { ...d, name: d.name.trim(), phone: d.phone.trim() };
    if (d.id) {
      const cur = empById(state, d.id);
      if (!cur) return fail('Không tìm thấy nhân sự');
      const moved = cur.positionId !== d.positionId || cur.branchId !== d.branchId;
      const ok = set(s => patchEmp(s, cur.id, e => logged({ ...e, ...clean, id: e.id, docs: d.docs || e.docs }, by,
        moved ? `Điều chuyển: ${positionById(s, cur.positionId)?.name} → ${positionById(s, d.positionId)?.name}` : 'Cập nhật hồ sơ')));
      return ok ? { ok, id: cur.id } : fail(...FULL);
    }
    const id = `e-${uid()}`;
    const now = Date.now();
    const emp: Employee = { ...clean, id, code: nextEmpCode(state), docs: d.docs || {}, licenses: [], ins: {}, createdAt: now, log: [{ at: now, by, text: 'Tạo hồ sơ nhân sự' }] };
    return set(s => ({ ...s, employees: [...s.employees, emp] })) ? { ok: true, id } : fail(...FULL);
  },

  setDoc(empId: string, key: DocKey, value: boolean, by: string) {
    set(s => patchEmp(s, empId, e => logged({ ...e, docs: { ...e.docs, [key]: value } }, by, `${value ? 'Đã nhận' : 'Bỏ đánh dấu'} giấy tờ: ${key}`)));
  },

  saveLicense(empId: string, lic: Omit<License, 'id'> & { id?: string }, by: string): Result {
    if (!lic.number.trim()) return fail('Nhập số giấy phép / chứng chỉ');
    if (!lic.issuedAt) return fail('Nhập ngày cấp');
    if (lic.expiresAt && lic.expiresAt <= lic.issuedAt) return fail('Ngày hết hạn phải sau ngày cấp');
    const clean: License = { ...lic, number: lic.number.trim(), id: lic.id || `lic-${uid()}` };
    set(s => patchEmp(s, empId, e => logged({ ...e, licenses: lic.id ? e.licenses.map(l => (l.id === lic.id ? clean : l)) : [...e.licenses, clean], docs: { ...e.docs, license: true } }, by, `Cập nhật giấy phép số ${clean.number}`)));
    return { ok: true, id: clean.id };
  },

  removeLicense(empId: string, licId: string, by: string) {
    set(s => patchEmp(s, empId, e => logged({ ...e, licenses: e.licenses.filter(l => l.id !== licId) }, by, 'Xóa giấy phép')));
  },

  setHealthCheck(empId: string, date: string, by: string) {
    set(s => patchEmp(s, empId, e => logged({ ...e, healthCheck: date, docs: { ...e.docs, health: true } }, by, `Khám sức khỏe ngày ${date}`)));
  },

  setMedicalLead(empId: string, value: boolean, by: string): Result {
    const e = empById(state, empId);
    if (!e) return fail('Không tìm thấy nhân sự');
    if (value && positionById(state, e.positionId)?.license !== 'gphn') return fail('Người phụ trách chuyên môn phải là bác sĩ có giấy phép hành nghề');
    set(s => ({
      ...s,
      employees: s.employees.map(x => {
        if (x.id === empId) return logged({ ...x, medicalLead: value }, by, value ? 'Chỉ định phụ trách chuyên môn chi nhánh' : 'Thôi phụ trách chuyên môn');
        // Mỗi chi nhánh một người phụ trách chuyên môn
        if (value && x.branchId === e.branchId && x.medicalLead && (!x.leftDate || x.leftDate >= today())) return logged({ ...x, medicalLead: false }, by, 'Thôi phụ trách chuyên môn (đã chỉ định người khác)');
        return x;
      }),
    }));
    return { ok: true };
  },

  /** Báo tăng / báo giảm BHXH */
  markInsurance(empId: string, kind: 'increase' | 'decrease', date: string, by: string): Result {
    const e = empById(state, empId);
    if (!e) return fail('Không tìm thấy nhân sự');
    if (kind === 'decrease' && !e.ins.increasedAt) return fail('Nhân sự chưa từng báo tăng BHXH');
    set(s => patchEmp(s, empId, x => logged({ ...x, ins: kind === 'increase' ? { increasedAt: date } : { ...x.ins, decreasedAt: date } }, by, kind === 'increase' ? `Báo tăng BHXH ngày ${date}` : `Báo giảm BHXH ngày ${date}`)));
    if (kind === 'decrease') {
      const o = state.offboardings.find(x => x.empId === empId && x.status === 'open');
      if (o && !o.tasks.insurance) set(s => patchOff(s, o.id, x => ({ ...x, tasks: { ...x.tasks, insurance: { at: Date.now(), by } } })));
    }
    return { ok: true };
  },

  /** Sửa một nhóm thông tin hồ sơ (thông tin chính, thuế – bảo hiểm, bổ sung, ngân hàng…) */
  saveProfile(empId: string, patch: Partial<Employee>, by: string, what = 'hồ sơ'): Result {
    const e = empById(state, empId);
    if (!e) return fail('Không tìm thấy nhân sự');
    if (patch.taxCode && !/^\d{10}(\d{3})?$/.test(patch.taxCode.replace(/[\s-]/g, ''))) return fail('Mã số thuế cá nhân gồm 10 hoặc 13 chữ số');
    if (patch.cccd && !/^\d{12}$/.test(patch.cccd)) return fail('Số CCCD gồm 12 chữ số');
    if (patch.bank && patch.bank.account && !/^\d{6,20}$/.test(patch.bank.account)) return fail('Số tài khoản ngân hàng chỉ gồm 6–20 chữ số');
    if (patch.dependents !== undefined && patch.dependents < 0) return fail('Số người phụ thuộc không hợp lệ');
    set(s => patchEmp(s, empId, x => logged({ ...x, ...patch }, by, `Cập nhật ${what}`)));
    return { ok: true };
  },

  /** Thêm / sửa một dòng trong danh sách hồ sơ (học vấn, kinh nghiệm, tài sản, khen thưởng – vi phạm) */
  saveItem<K extends ListKey>(empId: string, list: K, item: ListItem<K>, by: string): Result {
    const e = empById(state, empId);
    if (!e) return fail('Không tìm thấy nhân sự');
    const cur = (e[list] || []) as ListItem<K>[];
    const clean = { ...item, id: item.id || `${list}-${uid()}` } as ListItem<K>;
    if (list === 'assets') {
      const a = clean as AssetItem;
      if (!a.name.trim() || !a.code.trim()) return fail('Nhập tên và mã tài sản');
      const dup = state.employees.find(x => (x.assets || []).some(y => y.code.toUpperCase() === a.code.trim().toUpperCase() && !y.returnedAt && y.id !== a.id));
      if (dup) return fail(`Tài sản ${a.code} đang cấp cho ${dup.name}`);
    }
    if (list === 'records' && !(clean as RecordItem).title.trim()) return fail('Nhập nội dung');
    if (list === 'dependentList') {
      const dp = clean as Dependent;
      if (!dp.name?.trim() || !dp.relation) return fail('Nhập họ tên và quan hệ người phụ thuộc');
      if (!/^\d{4}-\d{2}$/.test(dp.from || '')) return fail('Chọn tháng bắt đầu giảm trừ');
      if (dp.relation === 'Con' && !dp.dob) return fail('Nhập ngày sinh của con (giảm trừ cho con dưới 18 tuổi hoặc đang đi học)');
      if (dp.taxCode && !/^\d{10}$/.test(dp.taxCode)) return fail('MST người phụ thuộc gồm 10 chữ số');
    }
    // Học vấn: chỉ một bằng cao nhất
    let next = item.id ? cur.map(x => (x.id === item.id ? clean : x)) : [...cur, clean];
    if (list === 'schools' && (clean as School).highest) next = next.map(x => (x.id === clean.id ? x : { ...x, highest: false }));
    set(s => patchEmp(s, empId, x => logged({ ...x, [list]: next, ...(list === 'dependentList' ? { dependents: (next as Dependent[]).filter(d => !d.to).length } : {}) }, by, `${item.id ? 'Sửa' : 'Thêm'} ${LIST_LABEL[list]}`)));
    return { ok: true, id: clean.id };
  },

  removeItem(empId: string, list: ListKey, id: string, by: string) {
    set(s => patchEmp(s, empId, x => {
      const rest = ((x[list] || []) as { id: string }[]).filter(y => y.id !== id);
      return logged({ ...x, [list]: rest, ...(list === 'dependentList' ? { dependents: (rest as Dependent[]).filter(d => !d.to).length } : {}) }, by, `Xóa ${LIST_LABEL[list]}`);
    }));
  },

  /** Thu hồi tài sản cấp phát (từng món hoặc toàn bộ khi nghỉ việc) */
  returnAssets(empId: string, ids: string[], date: string, by: string, condition?: string): Result {
    const e = empById(state, empId);
    if (!e) return fail('Không tìm thấy nhân sự');
    set(s => patchEmp(s, empId, x => logged({ ...x, assets: (x.assets || []).map(a => (ids.includes(a.id) && !a.returnedAt ? { ...a, returnedAt: date, condition: condition || a.condition } : a)) }, by, `Thu hồi ${ids.length} tài sản`)));
    return { ok: true };
  },

  /** Quyết định điều chuyển / bổ nhiệm / điều chỉnh lương. Hiệu lực từ hôm nay trở về trước thì áp ngay. */
  moveEmployee(empId: string, d: MoveDraft, by: string): Result {
    const e = empById(state, empId);
    if (!e) return fail('Không tìm thấy nhân sự');
    const v = validateMove(state, e, d, today());
    if (v.errors.length) return { ok: false, errors: v.errors, warnings: v.warnings };
    const codes = state.employees.flatMap(x => (x.moves || []).map(m => m.decisionNo));
    const m: MoveItem = {
      ...d, id: `mv-${uid()}`, decisionNo: d.decisionNo?.trim() || nextCode(codes, 'QĐ', d.date),
      fromPositionId: e.positionId, fromBranchId: e.branchId, prevSalary: salaryAt(state, e, d.date), by, applied: false,
      toPositionId: d.type === 'salary' ? e.positionId : d.toPositionId, toBranchId: d.type === 'salary' ? e.branchId : d.toBranchId,
    };
    const leavesLead = e.medicalLead && (m.toBranchId !== e.branchId || positionById(state, m.toPositionId)?.license !== 'gphn');
    set(s => applyDueMoves(patchEmp(s, empId, x => logged({ ...x, moves: [...(x.moves || []), m], ...(leavesLead && m.date <= today() ? { medicalLead: false } : {}) }, by,
      `Lập quyết định ${m.decisionNo}: ${MOVE_TYPE[m.type].label}${m.date > today() ? ` (hiệu lực ${m.date})` : ''}`))));
    return { ok: true, id: m.id, warnings: v.warnings };
  },

  /** Ghi nhận ngày nghỉ (phép năm, việc riêng, ốm, không lương, thai sản) */
  addLeave(empId: string, r: Omit<LeaveRecord, 'id' | 'by' | 'at'>, by: string): Result {
    const e = empById(state, empId);
    if (!e) return fail('Không tìm thấy nhân sự');
    const v = validateLeave(state, e, r, today());
    if (v.errors.length) return { ok: false, errors: v.errors, warnings: v.warnings };
    const rec: LeaveRecord = { ...r, id: `lv-${uid()}`, by, at: Date.now() };
    set(s => patchEmp(s, empId, x => logged({ ...x, leaveRecords: [...(x.leaveRecords || []), rec] }, by, `Ghi nhận ${LEAVE_KIND[r.kind].label.toLowerCase()} ${r.days} ngày (${r.from} → ${r.to})`)));
    return { ok: true, id: rec.id, warnings: v.warnings };
  },

  removeLeave(empId: string, id: string, by: string) {
    set(s => patchEmp(s, empId, x => logged({ ...x, leaveRecords: (x.leaveRecords || []).filter(r => r.id !== id) }, by, 'Xóa một đợt nghỉ')));
  },

  /** Hủy quyết định chưa tới ngày hiệu lực */
  cancelMove(empId: string, moveId: string, by: string): Result {
    const e = empById(state, empId);
    const m = e?.moves?.find(x => x.id === moveId);
    if (!e || !m) return fail('Không tìm thấy quyết định');
    if (m.applied) return fail('Quyết định đã có hiệu lực — lập quyết định mới để điều chỉnh');
    set(s => patchEmp(s, empId, x => logged({ ...x, moves: (x.moves || []).filter(y => y.id !== moveId) }, by, `Hủy quyết định ${m.decisionNo}`)));
    return { ok: true };
  },

  pause(empId: string, p: Employee['paused'] | undefined, by: string) {
    set(s => patchEmp(s, empId, e => logged({ ...e, paused: p }, by, p ? `Tạm hoãn HĐ từ ${p.from}: ${p.reason}` : 'Kết thúc tạm hoãn HĐ')));
  },

  /* ============================== Hợp đồng ============================== */

  saveContract(d: ContractDraft, by: string): Result {
    const { errors, warnings } = validateContract(state, d);
    if (errors.length) return { ok: false, errors, warnings };
    if (d.id) {
      const cur = state.contracts.find(c => c.id === d.id);
      if (!cur) return fail('Không tìm thấy HĐ');
      if (cur.signedAt) return fail('HĐ đã ký không sửa được — lập phụ lục hoặc HĐ mới');
      set(s => patchContract(s, cur.id, c => ({ ...c, ...d, id: c.id, code: c.code })));
      return { ok: true, id: cur.id, warnings };
    }
    const id = `c-${uid()}`;
    const c: Contract = { ...d, id, code: nextCode(state.contracts.map(x => x.code), CONTRACT_PREFIX[d.kind], d.start), createdBy: by, createdAt: Date.now() };
    const e = empById(state, d.empId);
    const ok = set(s => {
      const next = { ...s, contracts: [...s.contracts, c] };
      return e ? patchEmp(next, e.id, x => logged(x, by, `Lập ${c.kind === 'probation' ? 'thỏa thuận thử việc' : 'hợp đồng'} ${c.code}${c.signedAt ? ' (đã ký)' : ''}`)) : next;
    });
    return ok ? { ok, id, warnings } : fail(...FULL);
  },

  signContract(id: string, date: string, by: string): Result {
    const c = state.contracts.find(x => x.id === id);
    if (!c) return fail('Không tìm thấy HĐ');
    if (c.signedAt) return fail('HĐ đã ký');
    set(s => patchEmp(patchContract(s, id, x => ({ ...x, signedAt: date })), c.empId, e => logged(e, by, `Ký ${c.code}`)));
    return { ok: true };
  },

  cancelContract(id: string, by: string): Result {
    const c = state.contracts.find(x => x.id === id);
    if (!c) return fail('Không tìm thấy HĐ');
    if (c.signedAt) return fail('HĐ đã ký không hủy được — chấm dứt bằng thủ tục nghỉ việc hoặc ký HĐ mới');
    set(s => patchEmp(patchContract(s, id, x => ({ ...x, cancelled: true })), c.empId, e => logged(e, by, `Hủy HĐ chưa ký ${c.code}`)));
    return { ok: true };
  },

  /** Đánh giá thử việc. Không đạt → tự lập thủ tục nghỉ việc. */
  evaluateProbation(contractId: string, result: 'pass' | 'fail', by: string, opts: { score?: number; note?: string; lastDay?: string } = {}): Result {
    const c = state.contracts.find(x => x.id === contractId);
    if (!c || c.kind !== 'probation') return fail('Không tìm thấy thỏa thuận thử việc');
    if (c.evaluation) return fail('Đã có kết quả thử việc');
    if (result === 'fail' && !opts.note?.trim()) return fail('Ghi rõ lý do không đạt để thông báo cho người lao động');
    const ev = { result, by, at: Date.now(), score: opts.score, note: opts.note?.trim() || undefined };
    set(s => patchEmp(patchContract(s, contractId, x => ({ ...x, evaluation: ev })), c.empId, e => logged(e, by, `Kết quả thử việc: ${result === 'pass' ? 'Đạt' : 'Không đạt'}`)));
    if (result === 'fail') {
      const lastDay = opts.lastDay || (c.end && c.end < today() ? today() : c.end || today());
      return hrApi.startOffboarding({ empId: c.empId, type: 'probation_fail', noticeDate: today(), lastDay, reason: 'Không đạt thử việc', note: opts.note }, by);
    }
    return { ok: true };
  },

  /* ============================== Nghỉ việc ============================== */

  startOffboarding(d: { empId: string; type: LeaveType; noticeDate: string; lastDay: string; reason: string; note?: string; noRehire?: boolean }, by: string): Result {
    const e = empById(state, d.empId);
    if (!e) return fail('Không tìm thấy nhân sự');
    if (state.offboardings.some(o => o.empId === d.empId && o.status === 'open')) return fail('Nhân sự đang có thủ tục nghỉ việc');
    if (!d.lastDay || d.lastDay < e.hireDate) return fail('Ngày làm việc cuối không hợp lệ');
    if (!d.noticeDate || d.noticeDate > d.lastDay) return fail('Ngày nhận đơn / thông báo phải trước ngày nghỉ');
    if (!d.reason.trim()) return fail('Chọn lý do nghỉ');
    const id = `off-${uid()}`;
    const now = Date.now();
    const o: Offboarding = { ...d, id, tasks: {}, status: 'open', createdBy: by, createdAt: now, log: [{ at: now, by, text: 'Lập thủ tục nghỉ việc' }] };
    const ok = set(s => {
      const withOff = { ...s, offboardings: [...s.offboardings, o] };
      return patchEmp(withOff, e.id, x => logged({ ...x, leftDate: d.lastDay, leaveType: d.type, leaveReason: d.reason, noRehire: d.noRehire, medicalLead: x.medicalLead }, by, `Nghỉ việc từ ${d.lastDay}: ${d.reason}`));
    });
    return ok ? { ok, id } : fail(...FULL);
  },

  toggleOffTask(offId: string, task: OffTask, done: boolean, by: string): Result {
    const o = state.offboardings.find(x => x.id === offId);
    if (!o || o.status !== 'open') return fail('Thủ tục không ở trạng thái đang xử lý');
    const emp = empById(state, o.empId);
    const pending = emp ? unreturnedAssets(emp) : [];
    if (task === 'assets' && done && pending.length) return fail(`Còn ${pending.length} tài sản chưa thu hồi: ${pending.map(a => a.name).join(', ')}`);
    set(s => {
      let next = patchOff(s, offId, x => {
        const tasks = { ...x.tasks };
        if (done) tasks[task] = { at: Date.now(), by }; else delete tasks[task];
        return logged({ ...x, tasks }, by, `${done ? 'Hoàn thành' : 'Bỏ đánh dấu'}: ${task}`);
      });
      // Báo giảm BHXH ghi luôn vào hồ sơ
      if (task === 'insurance') next = patchEmp(next, o.empId, e => ({ ...e, ins: { ...e.ins, decreasedAt: done ? toDay() : undefined } }));
      return next;
    });
    return { ok: true };
  },

  completeOffboarding(offId: string, by: string): Result {
    const o = state.offboardings.find(x => x.id === offId);
    if (!o || o.status !== 'open') return fail('Thủ tục không ở trạng thái đang xử lý');
    const left = offTasksFor(state, o).filter(t => !o.tasks[t]);
    if (left.length) return fail(`Còn ${left.length} việc chưa xong`);
    if (o.lastDay > today()) return fail('Chưa tới ngày làm việc cuối');
    set(s => patchOff(s, offId, x => logged({ ...x, status: 'done', doneAt: Date.now() }, by, 'Hoàn tất thủ tục nghỉ việc')));
    return { ok: true };
  },

  /** Rút đơn / hủy thủ tục: nhân sự tiếp tục làm việc */
  cancelOffboarding(offId: string, by: string, note: string): Result {
    const o = state.offboardings.find(x => x.id === offId);
    if (!o || o.status !== 'open') return fail('Thủ tục không ở trạng thái đang xử lý');
    if (o.lastDay < today()) return fail('Nhân sự đã nghỉ, không hủy được');
    set(s => patchEmp(patchOff(s, offId, x => logged({ ...x, status: 'cancelled' }, by, `Hủy thủ tục: ${note || 'rút đơn'}`)), o.empId,
      e => logged({ ...e, leftDate: undefined, leaveType: undefined, leaveReason: undefined, noRehire: undefined }, by, 'Hủy thủ tục nghỉ việc, tiếp tục làm việc')));
    return { ok: true };
  },

  /* ============================== Định biên ============================== */

  setPlan(branchId: string, deptId: string, n: number) {
    set(s => ({ ...s, plan: { ...s.plan, [`${branchId}|${deptId}`]: Math.max(0, Math.round(n || 0)) } }));
  },

  /* ============================== Yêu cầu tuyển dụng ============================== */

  saveRequisition(d: ReqDraft, by: string): Result {
    const v = validateRequisition(state, d, today());
    if (v.errors.length) return { ok: false, errors: v.errors, warnings: v.warnings };
    if (d.id) {
      const cur = reqById(state, d.id);
      if (!cur || (cur.status !== 'pending' && cur.status !== 'rejected')) return fail('Chỉ sửa được yêu cầu chờ duyệt hoặc bị từ chối');
      set(s => patchReq(s, cur.id, r => logged({ ...r, ...d, id: r.id, status: 'pending', overPlan: v.overPlan, rejectReason: undefined }, by, 'Cập nhật yêu cầu, gửi duyệt lại')));
      return { ok: true, id: cur.id, warnings: v.warnings };
    }
    const id = `r-${uid()}`;
    const now = Date.now();
    const r: Requisition = { ...d, id, code: nextCode(state.requisitions.map(x => x.code), 'YC', today(), 2), status: 'pending', overPlan: v.overPlan, createdBy: by, createdAt: now, log: [{ at: now, by, text: 'Tạo yêu cầu tuyển dụng' }] };
    return set(s => ({ ...s, requisitions: [...s.requisitions, r] })) ? { ok: true, id, warnings: v.warnings } : fail(...FULL);
  },

  approveRequisition(id: string, by: string): Result {
    const r = reqById(state, id);
    if (!r || r.status !== 'pending') return fail('Yêu cầu không ở trạng thái chờ duyệt');
    set(s => patchReq(s, id, x => logged({ ...x, status: 'approved', approvedBy: by, approvedAt: Date.now() }, by, `Duyệt yêu cầu${x.overPlan ? ' (vượt định biên)' : ''}`)));
    return { ok: true };
  },

  rejectRequisition(id: string, by: string, reason: string): Result {
    const r = reqById(state, id);
    if (!r || r.status !== 'pending') return fail('Yêu cầu không ở trạng thái chờ duyệt');
    if (!reason.trim()) return fail('Nhập lý do từ chối');
    set(s => patchReq(s, id, x => logged({ ...x, status: 'rejected', rejectReason: reason.trim() }, by, `Từ chối: ${reason.trim()}`)));
    return { ok: true };
  },

  closeRequisition(id: string, by: string, note: string, cancel = false): Result {
    const r = reqById(state, id);
    if (!r || !['pending', 'approved', 'rejected'].includes(r.status)) return fail('Yêu cầu đã đóng');
    const open = state.candidates.filter(c => c.requisitionId === id && c.outcome === 'open');
    set(s => {
      let next = patchReq(s, id, x => logged({ ...x, status: cancel ? 'cancelled' : 'closed', closedAt: Date.now(), closeNote: note || undefined }, by, `${cancel ? 'Hủy' : 'Đóng'} yêu cầu${note ? `: ${note}` : ''}`));
      // Ứng viên còn trong quy trình được đóng lại (lưu hồ sơ)
      open.forEach(c => { next = patchCand(next, c.id, x => ({ ...x, outcome: 'withdrawn', closeReason: 'Vị trí đã đóng', closedAt: Date.now() })); });
      return next;
    });
    return { ok: true };
  },

  /* ============================== Ứng viên ============================== */

  addCandidate(d: Omit<Candidate, 'id' | 'stage' | 'outcome' | 'history' | 'createdBy' | 'appliedAt'> & { appliedAt?: number }, by: string): Result {
    const errors: string[] = [];
    if (!d.name.trim()) errors.push('Nhập họ tên ứng viên');
    if (!d.phone.replace(/\D/g, '')) errors.push('Nhập số điện thoại');
    if (!d.consent?.channel) errors.push('Ghi nhận ứng viên đồng ý xử lý dữ liệu cá nhân (Nghị định 13/2023)');
    const r = reqById(state, d.requisitionId);
    if (!r) errors.push('Chọn yêu cầu tuyển dụng');
    else if (r.status !== 'approved') errors.push('Yêu cầu tuyển dụng chưa được duyệt hoặc đã đóng');
    const dup = candidateDupCheck(state, d, today());
    errors.push(...dup.errors);
    if (errors.length) return { ok: false, errors, warnings: dup.warnings };
    const id = `k-${uid()}`;
    const at = d.appliedAt || Date.now();
    const c: Candidate = { ...d, name: d.name.trim(), id, stage: 'applied', outcome: 'open', appliedAt: at, createdBy: by, history: [{ stage: 'applied', at, by }] };
    return set(s => ({ ...s, candidates: [...s.candidates, c] })) ? { ok: true, id, warnings: dup.warnings } : fail(...FULL);
  },

  /** Chuyển bước tiếp theo (không cho nhảy cóc). Bước Offer dùng makeOffer, Nhận việc dùng hire. */
  advance(id: string, by: string, opts: { note?: string; score?: number } = {}): Result {
    const c = candById(state, id);
    if (!c || c.outcome !== 'open') return fail('Ứng viên không còn trong quy trình');
    const r = reqById(state, c.requisitionId);
    if (r?.status !== 'approved') return fail('Yêu cầu tuyển dụng đã đóng hoặc chưa được duyệt');
    const next = STAGES[STAGES.indexOf(c.stage) + 1] as Stage;
    if (next === 'offer' || next === 'hired') return fail(next === 'offer' ? 'Lập offer để chuyển sang bước Offer' : 'Dùng chức năng Nhận việc');
    if ((c.stage === 'interview' || c.stage === 'skill') && !opts.score) return fail('Chấm điểm phỏng vấn trước khi chuyển bước');
    const early = notYetInterviewed(c);
    if (early) return fail(early);
    const now = Date.now();
    set(s => patchCand(s, id, x => ({
      ...finishInterview(x, now), stage: next,
      history: [...x.history.map(h => (h.stage === x.stage && (opts.score || opts.note) ? { ...h, score: opts.score ?? h.score, note: opts.note || h.note } : h)), { stage: next, at: Date.now(), by }],
    })));
    return { ok: true };
  },

  makeOffer(id: string, salary: number, startDate: string, by: string, opts: { note?: string; score?: number } = {}): Result {
    const c = candById(state, id);
    if (!c || c.outcome !== 'open') return fail('Ứng viên không còn trong quy trình');
    if (c.stage !== 'skill' && c.stage !== 'offer') return fail('Ứng viên cần qua phỏng vấn chuyên môn trước khi offer');
    if (c.stage === 'offer' && c.offer?.status === 'accepted') return fail('Ứng viên đã nhận offer');
    const r = reqById(state, c.requisitionId);
    if (!r || r.status !== 'approved') return fail('Yêu cầu tuyển dụng đã đóng');
    if (!(salary > 0)) return fail('Nhập mức lương offer');
    if (!startDate || startDate < today()) return fail('Ngày nhận việc phải từ hôm nay trở đi');
    if (reqHired(state, r) >= r.qty) return fail(`Đã tuyển đủ ${r.qty} người cho yêu cầu ${r.code}`);
    const lic = licenseBlocker(state, c, today());
    if (lic) return fail(lic);
    if (c.license?.expiresAt && c.license.expiresAt <= startDate) return fail('Giấy phép hết hạn trước ngày nhận việc');
    const early = c.stage === 'skill' ? notYetInterviewed(c) : null;
    if (early) return fail(early);
    const need = offerNeedsApproval(r, salary);
    const offer = { salary, startDate, status: need ? 'need_approval' as const : 'sent' as const, at: Date.now() };
    set(s => patchCand(s, id, x => ({
      ...finishInterview(x, Date.now()), stage: 'offer', offer,
      history: x.stage === 'offer' ? x.history : [...x.history.map(h => (h.stage === x.stage && (opts.note || opts.score) ? { ...h, note: opts.note || h.note, score: opts.score ?? h.score } : h)), { stage: 'offer', at: Date.now(), by, note: need ? 'Ngoài khung lương — chờ duyệt' : undefined }],
    })));
    return { ok: true, warnings: need ? [`Lương offer ngoài khung ${r.code} — cần người có quyền duyệt trước khi gửi`] : undefined };
  },

  approveOffer(id: string, by: string): Result {
    const c = candById(state, id);
    if (c?.offer?.status !== 'need_approval') return fail('Offer không ở trạng thái chờ duyệt');
    set(s => patchCand(s, id, x => ({ ...x, offer: { ...x.offer!, status: 'sent', approvedBy: by } })));
    return { ok: true };
  },

  offerResult(id: string, accepted: boolean, by: string, reason?: string): Result {
    const c = candById(state, id);
    if (c?.offer?.status !== 'sent') return fail('Offer chưa được gửi');
    if (!accepted && !reason) return fail('Chọn lý do ứng viên từ chối');
    set(s => patchCand(s, id, x => ({
      ...x,
      offer: { ...x.offer!, status: accepted ? 'accepted' : 'declined', resolvedAt: Date.now(), declineReason: reason },
      ...(accepted ? {} : { outcome: 'withdrawn' as const, closeReason: reason, closedAt: Date.now() }),
    })));
    return { ok: true };
  },

  close(id: string, outcome: 'rejected' | 'withdrawn', reason: string, by: string): Result {
    const c = candById(state, id);
    if (!c || c.outcome !== 'open') return fail('Ứng viên không còn trong quy trình');
    if (!reason) return fail('Chọn lý do');
    set(s => patchCand(s, id, x => ({ ...x, outcome, closeReason: reason, closedAt: Date.now(), history: [...x.history.slice(0, -1), { ...x.history[x.history.length - 1], note: x.history[x.history.length - 1].note || `${outcome === 'rejected' ? 'Loại' : 'Ứng viên rút'}: ${reason}` }] })));
    return { ok: true };
  },

  /** Nhận việc: tạo hồ sơ nhân sự + thỏa thuận thử việc (chưa ký), tự đóng yêu cầu khi đủ chỉ tiêu */
  hire(id: string, by: string, opts: { probationSalary?: number } = {}): Result {
    const c = candById(state, id);
    if (!c || c.outcome !== 'open' || c.offer?.status !== 'accepted') return fail('Ứng viên chưa nhận offer');
    const r = reqById(state, c.requisitionId);
    if (!r || r.status !== 'approved') return fail('Yêu cầu tuyển dụng đã đóng');
    if (reqHired(state, r) >= r.qty) return fail(`Đã tuyển đủ ${r.qty} người cho yêu cầu ${r.code}`);
    const dup = candidateDupCheck(state, { phone: c.phone, cccd: c.cccd, id: c.id }, today());
    if (dup.errors.length) return { ok: false, errors: dup.errors };
    const now = Date.now();
    const empId = `e-${uid()}`;
    const emp: Employee = {
      id: empId, code: nextEmpCode(state), name: c.name, gender: c.gender, dob: c.dob, phone: c.phone, email: c.email, cccd: c.cccd,
      branchId: r.branchId, positionId: r.positionId, education: c.education, hireDate: c.offer.startDate,
      source: c.source, requisitionId: r.id, candidateId: c.id, ins: {}, createdAt: now,
      docs: { cv: true, ...(c.license?.verifiedAt ? { license: true } : {}) },
      licenses: c.license?.verifiedAt ? [{ id: `l-${uid()}`, kind: c.license.kind, number: c.license.number, issuedAt: c.license.issuedAt, expiresAt: c.license.expiresAt, scope: c.license.scope }] : [],
      log: [{ at: now, by, text: `Tuyển từ ${r.code}` }],
    };
    const lim = empProbationLimit(state, emp);
    const official = c.offer.salary;
    const prob: Contract = {
      id: `c-${uid()}`, code: nextCode(state.contracts.map(x => x.code), CONTRACT_PREFIX.probation, emp.hireDate), empId, kind: 'probation',
      start: emp.hireDate, end: probationEndOf(emp.hireDate, lim), salary: opts.probationSalary || Math.ceil((official * 0.85) / 100000) * 100000,
      officialSalary: official, createdBy: by, createdAt: now,
    };
    const full = reqHired(state, r) + 1 >= r.qty;
    set(s => {
      let next: HrState = { ...s, employees: [...s.employees, emp], contracts: [...s.contracts, prob] };
      next = patchCand(next, id, x => ({ ...x, stage: 'hired', outcome: 'hired', empId, history: [...x.history, { stage: 'hired', at: now, by }] }));
      if (full) {
        next = patchReq(next, r.id, x => logged({ ...x, status: 'closed', closedAt: now, closeNote: 'Đủ chỉ tiêu, đóng yêu cầu' }, by, 'Đủ chỉ tiêu, tự đóng yêu cầu'));
        next.candidates.filter(x => x.requisitionId === r.id && x.outcome === 'open').forEach(x => {
          next = patchCand(next, x.id, y => ({ ...y, outcome: 'withdrawn', closeReason: 'Vị trí đã đủ người', closedAt: now }));
        });
      }
      return next;
    });
    return { ok: true, id: empId };
  },

  /* ----- Lịch phỏng vấn ----- */

  /** Đặt / đổi lịch phỏng vấn cho vòng hiện tại (lịch cũ chuyển sang "đã đổi lịch") */
  scheduleInterview(id: string, d: { at: number; place: string; interviewer: string; note?: string }, by: string): Result {
    const c = candById(state, id);
    if (!c) return fail('Không tìm thấy ứng viên');
    const v = validateInterview(state, c, d);
    if (v.errors.length) return { ok: false, errors: v.errors, warnings: v.warnings };
    const now = Date.now();
    const old = pendingInterview(c);
    const iv: Interview = { id: `i-${uid()}`, stage: c.stage as Interview['stage'], at: d.at, place: d.place.trim(), interviewer: d.interviewer.trim(), note: d.note?.trim() || undefined, status: 'scheduled', by, createdAt: now };
    const ok = set(s => patchCand(s, id, x => ({
      ...x,
      interviews: [...(x.interviews || []).map(i => (i.id === old?.id ? { ...i, status: 'cancelled' as const, resolvedAt: now, resolvedNote: 'Đổi lịch' } : i)), iv],
    })));
    return ok ? { ok: true, id: iv.id, warnings: v.warnings } : fail(...FULL);
  },

  cancelInterview(id: string, by: string, note?: string): Result {
    const c = candById(state, id);
    const p = c && pendingInterview(c);
    if (!p) return fail('Không có lịch phỏng vấn đang chờ');
    set(s => patchCand(s, id, x => ({ ...x, interviews: (x.interviews || []).map(i => (i.id === p.id ? { ...i, status: 'cancelled' as const, resolvedAt: Date.now(), resolvedNote: note?.trim() || `Hủy lịch (${by})` } : i)) })));
    return { ok: true };
  },

  /** Ứng viên không đến phỏng vấn: đóng hồ sơ hoặc giữ lại để hẹn lần khác */
  interviewNoShow(id: string, by: string, close: boolean): Result {
    const c = candById(state, id);
    const p = c && pendingInterview(c);
    if (!c || !p) return fail('Không có lịch phỏng vấn đang chờ');
    if (p.at > Date.now()) return fail('Chưa tới giờ hẹn');
    const now = Date.now();
    set(s => patchCand(s, id, x => {
      const y = { ...x, interviews: (x.interviews || []).map(i => (i.id === p.id ? { ...i, status: 'noshow' as const, resolvedAt: now, resolvedNote: `Ghi nhận bởi ${by}` } : i)) };
      return close ? { ...y, outcome: 'withdrawn' as const, closeReason: 'Không đến phỏng vấn', closedAt: now } : y;
    }));
    return { ok: true };
  },

  /* ----- Giấy phép hành nghề ----- */

  /** Lưu giấy phép ứng viên khai; verified = đã đối chiếu bản gốc */
  saveCandidateLicense(id: string, d: Omit<CandidateLicense, 'verifiedBy' | 'verifiedAt'>, verified: boolean, by: string): Result {
    const c = candById(state, id);
    if (!c || c.outcome !== 'open') return fail('Ứng viên không còn trong quy trình');
    const v = validateCandidateLicense(d, today(), c.offer?.startDate);
    if (v.errors.length) return { ok: false, errors: v.errors, warnings: v.warnings };
    const lic: CandidateLicense = {
      kind: d.kind, number: d.number.trim(), issuedAt: d.issuedAt, expiresAt: d.expiresAt || undefined, scope: d.scope?.trim() || undefined, issuer: d.issuer?.trim() || undefined,
      ...(verified ? { verifiedBy: by, verifiedAt: Date.now() } : {}),
    };
    set(s => patchCand(s, id, x => ({ ...x, license: lic })));
    return { ok: true, warnings: v.warnings };
  },

  /* ----- Dữ liệu cá nhân ----- */

  recordConsent(id: string, channel: string, by: string): Result {
    const c = candById(state, id);
    if (!c || c.anonymizedAt) return fail('Không tìm thấy ứng viên');
    if (!channel) return fail('Chọn cách ứng viên xác nhận đồng ý');
    set(s => patchCand(s, id, x => ({ ...x, consent: { at: Date.now(), by, channel } })));
    return { ok: true };
  },

  /** Ứng viên rút lại đồng ý / yêu cầu xóa: đóng hồ sơ (nếu đang xử lý) và xóa dữ liệu nhận diện */
  eraseCandidate(id: string, by: string): Result {
    const c = candById(state, id);
    if (!c || c.anonymizedAt) return fail('Không tìm thấy ứng viên');
    if (c.outcome === 'hired') return fail('Ứng viên đã là nhân sự — dữ liệu quản lý tại hồ sơ nhân sự');
    const now = Date.now();
    set(s => patchCand(s, id, x => anonymized(x.outcome === 'open' ? { ...x, outcome: 'withdrawn', closeReason: `Ứng viên yêu cầu xóa dữ liệu (${by})`, closedAt: now } : x, now)));
    return { ok: true };
  },

  /** Xóa dữ liệu cá nhân các hồ sơ không trúng tuyển đã quá hạn lưu trữ; id = số hồ sơ đã xóa */
  purgeExpiredCandidates(): Result {
    const due = retentionDue(state, today());
    if (!due.length) return { ok: true, id: '0' };
    const ids = new Set(due.map(c => c.id));
    const now = Date.now();
    const ok = set(s => ({ ...s, candidates: s.candidates.map(c => (ids.has(c.id) ? anonymized(c, now) : c)) }));
    return ok ? { ok: true, id: String(due.length) } : fail(...FULL);
  },

  /* ----- Kho ứng viên ----- */

  /** Lưu / bỏ ứng viên khỏi kho (bỏ cả khỏi danh sách gợi ý tự động) */
  setPool(id: string, on: boolean, by: string): Result {
    const c = candById(state, id);
    if (!c || c.outcome === 'open' || c.outcome === 'hired' || c.anonymizedAt) return fail('Chỉ lưu hồ sơ đã đóng');
    set(s => patchCand(s, id, x => ({ ...x, pool: on ? { at: Date.now(), by } : undefined, poolHidden: on ? undefined : true })));
    return { ok: true };
  },

  /** Mời ứng viên trong kho ứng tuyển yêu cầu đang tuyển: tạo hồ sơ mới, giữ liên kết hồ sơ cũ */
  reinvite(id: string, reqId: string, by: string): Result {
    const c = candById(state, id);
    if (!c || c.anonymizedAt) return fail('Không tìm thấy ứng viên');
    if (c.outcome === 'open' || c.outcome === 'hired') return fail('Ứng viên đang trong quy trình');
    if (!c.consent || daysBetween(toDay(c.consent.at), today()) > CANDIDATE_RETENTION_DAYS) return fail('Đồng ý xử lý dữ liệu đã quá 12 tháng — liên hệ ứng viên xác nhận lại');
    const old = reqById(state, c.requisitionId);
    return hrApi.addCandidate({
      name: c.name, phone: c.phone, email: c.email, cccd: c.cccd, gender: c.gender, dob: c.dob, education: c.education,
      experienceYears: c.experienceYears, expectedSalary: c.expectedSalary, source: c.source, requisitionId: reqId,
      consent: c.consent, fromCandidateId: c.id,
      note: `Mời lại từ kho ứng viên (hồ sơ ${old?.code || ''}: ${c.closeReason || 'đã đóng'})`,
    }, by);
  },

  /* ----- Không đến nhận việc ----- */

  /**
   * Người đã nhận offer không đến nhận việc: hủy hồ sơ nhân sự + thỏa thuận thử việc tạo trước,
   * ứng viên về trạng thái từ chối, mở lại yêu cầu và các ứng viên bị đóng tự động khi yêu cầu đủ người.
   */
  cancelHire(empId: string, reason: string, by: string): Result {
    const e = empById(state, empId);
    if (!e) return fail('Không tìm thấy nhân sự');
    const chk = noShowCheck(state, e, today());
    if (!chk.ok) return fail(chk.reason!);
    if (!reason.trim()) return fail('Chọn lý do');
    const c = candById(state, e.candidateId)!;
    const r = reqById(state, c.requisitionId);
    const hiredAt = stageAt(c, 'hired') || 0;
    const now = Date.now();
    set(s => {
      let next: HrState = { ...s, employees: s.employees.filter(x => x.id !== e.id), contracts: s.contracts.filter(x => x.empId !== e.id) };
      next = patchCand(next, c.id, x => ({
        ...x, stage: 'offer', outcome: 'withdrawn', empId: undefined, closeReason: `Không đến nhận việc: ${reason.trim()}`, closedAt: now,
        offer: x.offer ? { ...x.offer, status: 'declined', resolvedAt: now, declineReason: 'Không đến nhận việc' } : x.offer,
        history: x.history.filter(h => h.stage !== 'hired'),
        noShow: { at: now, by, empCode: e.code, reason: reason.trim() },
      }));
      if (r && r.status === 'closed') {
        next = patchReq(next, r.id, x => logged({ ...x, status: 'approved', closedAt: undefined, closeNote: undefined }, by, `Mở lại: ${e.name} không đến nhận việc`));
        // Ứng viên bị đóng tự động lúc yêu cầu đủ người thì quay lại quy trình
        next.candidates.filter(x => x.requisitionId === r.id && x.outcome === 'withdrawn' && x.closeReason === 'Vị trí đã đủ người' && (x.closedAt || 0) >= hiredAt).forEach(x => {
          next = patchCand(next, x.id, y => ({ ...y, outcome: 'open', closeReason: undefined, closedAt: undefined }));
        });
      }
      return next;
    });
    return { ok: true, id: c.id };
  },

  loadDemo(by?: string) { set(() => demoState(Date.now(), by)); },
};

/** HĐ đang áp dụng của nhân sự (tiện cho giao diện) */
export const contractNow = (empId: string) => currentContract(state, empId, today());
export const contractsOf = (empId: string) => empContracts(state, empId);
